import type {
  AITeacherPhase,
  AITeacherQuizResponse,
  AITeacherSession,
  AITeacherTeacherHelpRequest,
  AITeacherTopicOutcome,
} from './types'

export const MAX_REMOTE_ANALYSIS_RETRIES = 2

export function resolveAITeacherRestoredPhase(
  phase: AITeacherPhase | undefined,
  hasResult: boolean,
  hasRestorableCertificationRound = false,
  hasTeacherHelpRequest = false,
): AITeacherPhase {
  if (hasResult && hasTeacherHelpRequest) return 'topic-done'
  if (phase === 'topic-certification') {
    return hasRestorableCertificationRound ? 'topic-certification' : 'topic-done'
  }
  if (phase === 'certification-result') return 'certification-result'
  if (phase === 'loading' && hasResult) return 'greeting'
  return phase ?? (hasResult ? 'greeting' : 'loading')
}

export function normalizeAITeacherSession(session: AITeacherSession): AITeacherSession {
  return {
    ...session,
    remediationLoops: session.remediationLoops ?? {},
    scaffoldLevels: session.scaffoldLevels ?? {},
    topicOutcomes: session.topicOutcomes ?? {},
    certificationRounds: session.certificationRounds ?? {},
    teacherHelpRequests: session.teacherHelpRequests ?? {},
  }
}

export function canRestoreAITeacherCertificationRound(
  session: AITeacherSession,
  topicIndex: number,
  questionManifest: Array<{ questionId: string; contentFingerprint: string }>,
): boolean {
  const round = session.certificationRounds?.[topicIndex]
  if (!round || round.topicIndex !== topicIndex || round.questionManifest.length !== 5) return false
  const currentLoopCount = session.remediationLoops?.[topicIndex] ?? 0
  if (!Number.isInteger(currentLoopCount) || currentLoopCount < 0 || currentLoopCount >= 2) return false
  if (round.roundNumber !== currentLoopCount) return false
  if (questionManifest.length !== 5) return false
  const expected = new Map(questionManifest.map((question) => [question.questionId, question.contentFingerprint]))
  if (expected.size !== 5 || new Set(questionManifest.map((question) => question.contentFingerprint)).size !== 5) return false
  if (new Set(round.questionManifest.map((question) => question.questionId)).size !== 5
    || new Set(round.questionManifest.map((question) => question.contentFingerprint)).size !== 5) return false
  if (round.questionManifest.some((question) => expected.get(question.questionId) !== question.contentFingerprint)) return false
  if (round.attempts.length > 5 || round.attempts.some((attempt) => (
    attempt.assessmentSessionId !== round.id
    || attempt.studentId !== session.studentId
    || attempt.evidenceKind !== 'certification'
    || attempt.hintCount !== 0
    || attempt.retryCount !== 0
    || !attempt.contentFingerprint
    || expected.get(attempt.exerciseId) !== attempt.contentFingerprint
  ))) return false
  const attemptIds = new Set(round.attempts.map((attempt) => attempt.exerciseId))
  const attemptFingerprints = new Set(round.attempts.map((attempt) => attempt.contentFingerprint))
  if (attemptIds.size !== round.attempts.length || attemptFingerprints.size !== round.attempts.length) return false

  const responses = session.quizResponses.filter((response) => (
    response.topicIndex === topicIndex && responsePhase(response) === 'topic-certification'
  ))
  if (responses.some((response) => (
    !Number.isInteger(response.questionIndex)
    || response.questionIndex < 0
    || response.questionIndex >= questionManifest.length
  ))) return false
  if (new Set(responses.map((response) => response.questionIndex)).size !== responses.length) return false
  const submittedResponses = responses.filter((response) => response.submitted)
  if (submittedResponses.length !== round.attempts.length) return false
  const attemptsByQuestionId = new Map(round.attempts.map((attempt) => [attempt.exerciseId, attempt]))
  return submittedResponses.every((response) => {
    const question = questionManifest[response.questionIndex]
    const attempt = question ? attemptsByQuestionId.get(question.questionId) : undefined
    return Boolean(attempt)
      && response.correct === attempt?.correct
      && response.userAnswer === attempt?.studentAnswer
  })
}

export function resolveAITeacherCertificationFailure(input: {
  topicIndex: number
  currentLoopCount: number
  score: number
  completedAt: string
}): {
  nextLoopCount: number
  scaffoldLevel: 2
  outcome: AITeacherTopicOutcome
  teacherHelpRequest?: AITeacherTeacherHelpRequest
} {
  const nextLoopCount = Math.min(2, Math.max(0, input.currentLoopCount) + 1)
  const needsTeacherHelp = nextLoopCount >= 2
  return {
    nextLoopCount,
    scaffoldLevel: 2,
    outcome: {
      passed: false,
      score: input.score,
      status: needsTeacherHelp ? 'teacher-help' : 'retrying',
      completedAt: input.completedAt,
    },
    ...(needsTeacherHelp ? {
      teacherHelpRequest: {
        topicIndex: input.topicIndex,
        reason: 'certification-failed-twice' as const,
        createdAt: input.completedAt,
      },
    } : {}),
  }
}

export function canRetryRemoteAnalysis(retryCount: number): boolean {
  return Number.isFinite(retryCount)
    && retryCount >= 0
    && retryCount < MAX_REMOTE_ANALYSIS_RETRIES
}

export interface AITeacherQuizItemSnapshot {
  userAnswer: string
  feedback: string
  correct: boolean
  submitted: boolean
}

export function responsePhase(response: AITeacherQuizResponse): 'topic-quiz' | 'topic-certification' {
  return response.phase ?? 'topic-quiz'
}

export function upsertAITeacherQuizResponse(
  responses: AITeacherQuizResponse[],
  next: AITeacherQuizResponse,
): AITeacherQuizResponse[] {
  const index = responses.findIndex((response) => (
    response.topicIndex === next.topicIndex
    && response.questionIndex === next.questionIndex
    && responsePhase(response) === responsePhase(next)
  ))
  if (index < 0) return [...responses, next]
  return responses.map((response, responseIndex) => responseIndex === index ? next : response)
}

export function mergeAITeacherQuizResponses(
  responses: AITeacherQuizResponse[],
  topicIndex: number,
  phase: 'topic-quiz' | 'topic-certification',
  items: AITeacherQuizItemSnapshot[],
): AITeacherQuizResponse[] {
  const stored = new Map(
    responses
      .filter((response) => response.topicIndex === topicIndex && responsePhase(response) === phase)
      .map((response) => [response.questionIndex, response]),
  )
  const current = items.map((item, questionIndex): AITeacherQuizResponse => {
    const previous = stored.get(questionIndex)
    return {
      topicIndex,
      questionIndex,
      phase,
      userAnswer: item.userAnswer || previous?.userAnswer || '',
      submitted: item.submitted || previous?.submitted || false,
      correct: item.submitted ? item.correct : previous?.correct,
      feedback: item.feedback || previous?.feedback || '',
    }
  })
  return [
    ...responses.filter((response) => (
      response.topicIndex !== topicIndex || responsePhase(response) !== phase
    )),
    ...current,
  ]
}
